import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { Field, Form, Formik } from 'formik';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';

import { ReactComponent as EyeOn } from '../../../assets/images/svg/eye-on.svg';
import { ReactComponent as EyeOff } from '../../../assets/images/svg/eye-off.svg';
import { ReactComponent as Lock } from '../../../assets/images/svg/lock.svg';
import { ReactComponent as InfoIcon } from '../../../assets/images/svg/info-filled-blue.svg';
import { ReactComponent as AmberAlertTriangle } from '../../../assets/images/svg/amber-alert-triangle.svg';
import { ReactComponent as Close } from '../../../assets/images/svg/close.svg';
import BottomDrawer from '../../Drawer/Drawer';
import { ModalClose } from '../../Modal/Modal';
import Button from '../../Button/Button';
import SectionLoader from '../../Loader/SectionLoader';
import { hashedMask, isPositiveInteger } from '../../../util/helpers/ssnHelper';
import themeColors from '../../../assets/styles/themeColors';
import TextField from '../../InputFields/TextField';
import { InputLabel } from '../../InputFields/Components/InputComponents';
import FormContainer from '../../FormContainer/FormContainer';
import { ErrorMessage } from '../../InputFields/ControlledInput';
import Anchor from '../../Anchor/Anchor';
import { postUserSsn } from '../../../store/actions/user/userActions';
import { fetchAccountSettings } from '../../../store/actions/account/accountActions';
import { getErrorCodeFromAPIResponse } from '../../../util/helpers/rejectErrorCodeHelper';
import { FullSsnOrderType } from '../../../util/enum/api/fullSsnOrderTypes';
import { mediaBelow, pxToRem, pxToRemMd } from '../../../assets/styles/helper';
import { variables } from '../../../assets/styles/variables';

const IconButton = styled.button`
  outline: none;
  width: auto;
  height: auto;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const DrawerHero = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding-top: ${pxToRem(4)};
  margin-bottom: ${pxToRem(4)};
`;

const DrawerIconWrapper = styled.div`
  margin: ${pxToRem(16)} 0;
`;

const DrawerTitle = styled.h2`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(16)};
  font-weight: 600;
  color: ${themeColors.colorTextPrimary};
  text-align: center;
  margin: 0 0 ${pxToRem(16)};
  padding: 0 ${pxToRem(8)};
  line-height: 1.35;
`;

const NoteBoxIndividualJoint = styled.div`
  padding: ${pxToRem(12)};
  color: ${themeColors.colorBlack};
  background-color: ${themeColors.colorLightYellow};
  border: 1px solid ${themeColors.colorRowBrThree};
  border-radius: ${pxToRem(4)};
  margin-bottom: ${pxToRem(16)};
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
  line-height: ${pxToRem(19)};
`;

const NoteBoxTrust = styled.div`
  padding: ${pxToRem(12)};
  color: ${themeColors.colorBlack};
  background-color: ${themeColors.colorLightYellow};
  border: 1px solid ${themeColors.colorRowBrThree};
  border-radius: ${pxToRem(4)};
  margin-bottom: ${pxToRem(16)};
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
  line-height: ${pxToRem(19)};
`;

/** Extends InputLabel so 14px wins over the shared 16px rule (nested selectors lose injection order). */
const SfhSsnInputLabel = styled(InputLabel)`
  font-size: ${pxToRem(14)};
  line-height: ${pxToRem(19)};
  font-weight: 400;
`;

const SfhSsnFieldLabelWrap = styled.div`
  margin-bottom: ${pxToRem(4)};
`;

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;

  button {
    position: absolute;
    right: ${pxToRem(11)};
    bottom: ${pxToRem(13)};
  }

  svg {
    width: 24px;
    height: 24px;
    color: ${themeColors.colorInputPlaceholder};
  }

  input {
    padding-right: 42px;
    font-size: ${pxToRem(14)};
    line-height: 1.4;

    &::placeholder {
      font-size: ${pxToRem(14)};
      color: ${themeColors.colorTextSecondary};
      font-weight: 400;
    }

    ${mediaBelow(variables.breakpoints.bpXl)} {
      font-size: ${pxToRemMd(14)};

      &::placeholder {
        font-size: ${pxToRemMd(14)};
      }
    }
  }
`;

const InfoSecureContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: ${pxToRem(8)};
`;

const LockIconWrap = styled.div`
  height: 16px;
  width: 16px;
  margin-right: 4px;
`;

const InfoSecure = styled.span`
  font-size: ${pxToRem(13)};
  color: ${themeColors.colorTextSecondary};
`;

const FooterActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 0 ${pxToRem(16)} ${pxToRem(16)};
  margin: -${pxToRem(16)} 0;
  width: 100%;
`;

const IrsButtonRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: ${pxToRem(12)};
  width: 100%;

  button {
    font-family: ${variables.family.goldSilver}, sans-serif;
    font-size: ${pxToRem(16)};
    height: ${pxToRem(44)};
    width: 100%;
  }
`;

const LearnMoreLinkWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: ${pxToRem(16)};
`;

export const LearnMoreLinkText = styled.div`
  font-size: 14px;
  text-decoration: underline;
  text-decoration-style: solid;
  color: ${themeColors.colorLinkPrimary};
`;

const toastSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/**
 * Anchored to Sell From Home quantity `SectionLoader` / `LoaderContainer` (position: relative),
 * i.e. the modal body column — not the viewport — so it stays inside the white modal card.
 */
const IrsWarningToastRoot = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 60;
  pointer-events: none;
  box-sizing: border-box;
  padding: 24px 16px 0;
`;

const IrsWarningToastStack = styled.div`
  pointer-events: none;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-sizing: border-box;
  align-items: stretch;
  width: 100%;
`;

const IrsWarningToastCard = styled.div`
  pointer-events: auto;
  position: relative;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  border-radius: ${variables.borderRadius.borderRadiusMd};
  background-color: ${themeColors.colorWhite};
  box-shadow: 0 3px 8px 0 rgba(0, 0, 0, 0.24);
  overflow: hidden;
  animation: ${toastSlideIn} 0.25s ease-out;
`;

const IrsWarningToastStripe = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: ${pxToRem(6)};
  border-radius: ${pxToRem(4)} 0 0 ${pxToRem(4)};
  background-color: ${themeColors.colorWarning};
`;

const IrsWarningToastRow = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: ${pxToRem(12)};
  padding: ${pxToRem(16)} ${pxToRem(40)} ${pxToRem(16)} ${pxToRem(16)};
`;

const IrsWarningToastIconWrap = styled.div`
  flex-shrink: 0;
  padding-top: ${pxToRem(2)};

  svg {
    width: ${pxToRem(24)};
    height: ${pxToRem(24)};
    display: block;
  }
`;

const IrsWarningToastCopy = styled.div`
  flex: 1;
  min-width: 0;
  font-family: ${variables.family.goldSilver}, sans-serif;
  padding-right: ${pxToRem(4)};
`;

const IrsWarningToastTitle = styled.p`
  margin: 0 0 ${pxToRem(4)};
  font-size: ${pxToRem(16)};
  font-weight: 600;
  color: ${themeColors.colorTextPrimary};
  line-height: 1.35;
`;

const IrsWarningToastMessage = styled.p`
  margin: 0;
  font-size: ${pxToRem(14)};
  font-weight: 400;
  color: ${themeColors.colorTextSecondary};
  line-height: 1.5;
`;

/** Same control + SVG sizing as SFH wizard `ModalClose` (SellFromHomeWizard header). */
const IrsToastModalClose = styled(ModalClose)`
  position: absolute;
  top: ${pxToRem(12)};
  right: ${pxToRem(12)};
`;

const TOP_TOAST_AUTO_CLOSE_MS = 15000;
const MAX_IRS_TOASTS = 2;

const FORM_ID = 'sfh-irs-ssn-form';
const SSN_TOO_MANY_ATTEMPTS = 'SSNTooManyAttempts';

const SellFromHomeIrsSsnDrawer = ({
  isOpen,
  onClose,
  onSuccess,
  onLockedOut,
  copy,
  accountUid,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [actualSsn, setActualSsn] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [topToasts, setTopToasts] = useState([]);
  const [isLockoutPending, setIsLockoutPending] = useState(false);
  const toastTimersRef = useRef(new Map());
  // Optional per-toast onDismiss callbacks. Fired exactly once when the toast
  // is removed (auto-close, user click, or drawer close via clearAllToasts).
  const toastDismissRef = useRef(new Map());

  const maskedSsn = useMemo(() => hashedMask(actualSsn, true, isVisible), [
    actualSsn,
    isVisible,
  ]);

  const removeToast = useCallback((toastId) => {
    const timers = toastTimersRef.current;
    const tid = timers.get(toastId);
    if (tid != null) {
      window.clearTimeout(tid);
      timers.delete(toastId);
    }
    const dismissals = toastDismissRef.current;
    const onDismiss = dismissals.get(toastId);
    dismissals.delete(toastId);
    setTopToasts((prev) => prev.filter((x) => x.id !== toastId));
    if (typeof onDismiss === 'function') {
      onDismiss();
    }
  }, []);

  const clearAllToasts = useCallback(() => {
    const timers = toastTimersRef.current;
    timers.forEach((tid) => window.clearTimeout(tid));
    timers.clear();
    const dismissals = toastDismissRef.current;
    const pending = Array.from(dismissals.values());
    dismissals.clear();
    setTopToasts([]);
    pending.forEach((cb) => {
      if (typeof cb === 'function') cb();
    });
  }, []);

  const enqueueToast = useCallback(
    ({ title, message, onDismiss }) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
      const timers = toastTimersRef.current;
      const dismissals = toastDismissRef.current;
      setTopToasts((prev) => {
        const next = [{ id, title, message }, ...prev];
        if (next.length <= MAX_IRS_TOASTS) {
          return next;
        }
        next.slice(MAX_IRS_TOASTS).forEach((toast) => {
          const tid = timers.get(toast.id);
          if (tid != null) {
            window.clearTimeout(tid);
            timers.delete(toast.id);
          }
          dismissals.delete(toast.id);
        });
        return next.slice(0, MAX_IRS_TOASTS);
      });
      if (typeof onDismiss === 'function') {
        dismissals.set(id, onDismiss);
      }
      const tid = window.setTimeout(() => {
        removeToast(id);
      }, TOP_TOAST_AUTO_CLOSE_MS);
      timers.set(id, tid);
    },
    [removeToast],
  );

  useEffect(() => {
    if (!isOpen) {
      setActualSsn('');
      setIsVisible(false);
      setError('');
      setIsLockoutPending(false);
      clearAllToasts();
    }
  }, [isOpen, clearAllToasts]);

  useEffect(() => {
    const timers = toastTimersRef.current;
    return () => {
      timers.forEach((tid) => window.clearTimeout(tid));
      timers.clear();
    };
  }, []);

  const {
    isTrust,
    isIndividual,
    ssnAccountType,
    primaryHolderDisplayName,
  } = copy;

  const noteBody = useMemo(() => {
    if (isTrust) {
      return <>{t('irs.tin.note')}</>;
    }
    if (isIndividual && primaryHolderDisplayName) {
      return (
        <>
          {t('irs.ssn.primaryHolderNote1')}
          <strong>{primaryHolderDisplayName} </strong>
          {t('irs.ssn.primaryHolderNote2')}
        </>
      );
    }
    return <>{t('irs.ssn.note')}</>;
  }, [t, isTrust, isIndividual, primaryHolderDisplayName]);

  const NoteBox = isTrust ? NoteBoxTrust : NoteBoxIndividualJoint;

  const handleSubmit = useCallback(() => {
    if (actualSsn === '') return;

    const formatted = `${actualSsn.slice(0, 3)}-${actualSsn.slice(
      3,
      5,
    )}-${actualSsn.slice(5, 9)}`;

    if (formatted.length < 11) {
      setError(
        isTrust
          ? t('fields.errors.validation.tinNotValid')
          : t('fields.errors.validation.ssnNotValid'),
      );
      return;
    }

    if (
      !isPositiveInteger(formatted.slice(0, 3)) ||
      !isPositiveInteger(formatted.slice(4, 6)) ||
      !isPositiveInteger(formatted.slice(7, 11))
    ) {
      setError(
        isTrust
          ? t('fields.errors.validation.tinDigits')
          : t('fields.errors.validation.ssnDigits'),
      );
      return;
    }

    if (!accountUid) return;

    const payload = {
      data: {
        AccountUid: accountUid,
        Tin: formatted,
        FullSsn: formatted,
        AccountType: ssnAccountType,
        // Tells BE this submission originated from the Sell From Home flow
        // (standard Sell defaults to `FullSsnOrderType.Sell` on the BE side).
        OrderType: FullSsnOrderType.SellFromHome,
      },
      setIsLoading,
      onSuccess: () => {
        dispatch(fetchAccountSettings({ accountUid }));
        onSuccess();
      },
      onError: (apiError) => {
        const errorCode = getErrorCodeFromAPIResponse(apiError);
        const isTooManyAttempts = errorCode === SSN_TOO_MANY_ATTEMPTS;

        if (isTooManyAttempts) {
          // On lockout, surface the toast and block further submits. Only
          // after the user (or the auto-close timer) dismisses the toast do
          // we refresh the settings API so `selectIsSellFromHomeRestricted`
          // picks up the lock. We intentionally skip `accounts/{accountUid}`
          // here — settings is the source of truth for the SFH restriction.
          setIsLockoutPending(true);
          enqueueToast({
            title: t(
              isTrust
                ? `irs.tin.error.${SSN_TOO_MANY_ATTEMPTS}.title`
                : `irs.ssn.error.${SSN_TOO_MANY_ATTEMPTS}.title`,
            ),
            message: t(
              isTrust
                ? `irs.tin.error.${SSN_TOO_MANY_ATTEMPTS}.message`
                : `irs.ssn.error.${SSN_TOO_MANY_ATTEMPTS}.message`,
            ),
            onDismiss: () => {
              dispatch(fetchAccountSettings({ accountUid }));
              if (typeof onLockedOut === 'function') {
                onLockedOut();
              }
            },
          });
          return;
        }

        if (errorCode) {
          enqueueToast({
            title: t(
              isTrust
                ? `irs.tin.error.${errorCode}.title`
                : `irs.ssn.error.${errorCode}.title`,
            ),
            message: t(
              isTrust
                ? `irs.tin.error.${errorCode}.message`
                : `irs.ssn.error.${errorCode}.message`,
            ),
          });
        }
      },
    };
    dispatch(postUserSsn(payload));
  }, [
    actualSsn,
    isTrust,
    t,
    accountUid,
    ssnAccountType,
    dispatch,
    onSuccess,
    onLockedOut,
    enqueueToast,
  ]);

  const handleChange = useCallback(
    (event) => {
      // While a too-many-attempts lockout toast is pending acknowledgement we
      // freeze the input so a stray keystroke doesn't auto-dismiss the toast
      // (and therefore fire the refresh / lockout flow) before the user has
      // had a chance to read it.
      if (isLockoutPending) return;

      const newValue = event.target.value;
      if (newValue.length > 11) return;

      const prevValue = maskedSsn;
      let next = actualSsn;

      if (newValue.length > prevValue.length) {
        next = `${actualSsn}${newValue.slice(-1)}`;
      } else {
        const charsRemovedCount = prevValue.length - newValue.length;
        next = actualSsn.slice(0, actualSsn.length - charsRemovedCount);
      }

      setError('');
      clearAllToasts();
      setActualSsn(next);
    },
    [actualSsn, maskedSsn, clearAllToasts, isLockoutPending],
  );

  const toggleVisibility = useCallback(() => setIsVisible((v) => !v), []);

  const footer = (
    <FooterActions>
      <IrsButtonRow>
        <Button
          variant="primaryOutlined"
          textTransform="none"
          onClick={onClose}
          data-cy="button-sfh-irs-close"
        >
          {t('common.close')}
        </Button>
        <Button
          variant="primary"
          textTransform="none"
          type="submit"
          form={FORM_ID}
          disabled={maskedSsn?.length !== 11 || isLoading || isLockoutPending}
          data-cy="button-sfh-irs-agree"
        >
          {t('common.agreeAndContinue')}
        </Button>
      </IrsButtonRow>
      <LearnMoreLinkWrap>
        <LearnMoreLinkText>
          <Anchor
            bold
            type="website"
            value={copy.learnMoreHref}
            text={copy.learnMoreText}
          />
        </LearnMoreLinkText>
      </LearnMoreLinkWrap>
    </FooterActions>
  );

  const topToastLayer = isOpen && topToasts.length > 0 && (
    <IrsWarningToastRoot data-cy="sfh-irs-top-toast-stack">
      <IrsWarningToastStack>
        {topToasts.map((toast) => (
          <IrsWarningToastCard
            key={toast.id}
            role="alert"
            data-cy="sfh-irs-top-toast"
            data-toast-id={toast.id}
          >
            <IrsWarningToastStripe aria-hidden />
            <IrsWarningToastRow>
              <IrsWarningToastIconWrap aria-hidden>
                <AmberAlertTriangle />
              </IrsWarningToastIconWrap>
              <IrsWarningToastCopy>
                <IrsWarningToastTitle>{toast.title}</IrsWarningToastTitle>
                <IrsWarningToastMessage>{toast.message}</IrsWarningToastMessage>
              </IrsWarningToastCopy>
            </IrsWarningToastRow>
            <IrsToastModalClose
              type="button"
              aria-label={t('common.close')}
              onClick={() => removeToast(toast.id)}
              data-cy="sfh-irs-top-toast-close"
            >
              <Close />
            </IrsToastModalClose>
          </IrsWarningToastCard>
        ))}
      </IrsWarningToastStack>
    </IrsWarningToastRoot>
  );

  return (
    <>
      {topToastLayer}
      <BottomDrawer
        isOpen={isOpen}
        onClose={onClose}
        hideHeader
        hideClose
        title=""
        footer={footer}
        maxHeight="85%"
      >
        <SectionLoader isLoading={isLoading}>
          <DrawerHero>
            <DrawerIconWrapper>
              <InfoIcon width={35} height={35} aria-hidden />
            </DrawerIconWrapper>
            <DrawerTitle>{copy.title}</DrawerTitle>
          </DrawerHero>

          <NoteBox>
            <strong>{t('common.important')}</strong>
            {noteBody}
          </NoteBox>

          <Formik
            initialValues={{ Ssn: '' }}
            onSubmit={handleSubmit}
            validateOnBlur
          >
            {() => (
              <Form id={FORM_ID}>
                <SfhSsnFieldLabelWrap>
                  <SfhSsnInputLabel htmlFor="Ssn">
                    {copy.inputLabel}
                  </SfhSsnInputLabel>
                </SfhSsnFieldLabelWrap>
                <FormContainer hasError>
                  <Container>
                    <Field
                      component={TextField}
                      name="Ssn"
                      placeholder={copy.inputPlaceholder}
                      color={themeColors.colorBlack}
                      value={maskedSsn}
                      onChange={handleChange}
                      autocomplete="off"
                      hideError
                    />
                    <IconButton
                      type="button"
                      title={isVisible ? 'Hide SSN' : 'Show SSN'}
                      onClick={toggleVisibility}
                    >
                      {isVisible ? <EyeOn /> : <EyeOff />}
                    </IconButton>
                  </Container>
                </FormContainer>
                {error && (
                  <ErrorMessage error={error} name="Ssn">
                    {error}
                  </ErrorMessage>
                )}
                <InfoSecureContainer>
                  <LockIconWrap>
                    <Lock />
                  </LockIconWrap>
                  <InfoSecure>{copy.secureFootnote}</InfoSecure>
                </InfoSecureContainer>
              </Form>
            )}
          </Formik>
        </SectionLoader>
      </BottomDrawer>
    </>
  );
};

SellFromHomeIrsSsnDrawer.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onLockedOut: PropTypes.func,
  accountUid: PropTypes.string,
  copy: PropTypes.shape({
    title: PropTypes.string.isRequired,
    learnMoreHref: PropTypes.string.isRequired,
    learnMoreText: PropTypes.string.isRequired,
    secureFootnote: PropTypes.string.isRequired,
    inputLabel: PropTypes.string.isRequired,
    inputPlaceholder: PropTypes.string.isRequired,
    isTrust: PropTypes.bool,
    isIndividual: PropTypes.bool,
    primaryHolderDisplayName: PropTypes.string,
    ssnAccountType: PropTypes.string,
  }).isRequired,
};

export default SellFromHomeIrsSsnDrawer;
